import ResponseType from '@/lib/ResponseType';
import Section from '@/lib/Section';
import Choice from '@/lib/Choice';
import Condition from '@/lib/Condition';
import Response from '@/lib/Response';
import { TypedEvent } from '@/lib/TypedEvent';
import { EntityChoice } from '@/lib/EntityChoice';
import store from '@/store';
import { api } from '@/Api';
import { AxiosResponse } from 'axios';
import { IconRegistry } from '@/lib/IconRegistry';
import DatePickerQuestion from '@/components/DatePickerQuestion.vue';
import NumberQuestion from '@/components/NumberQuestion.vue';
import moment from 'moment';
import { ArraySectionItem } from "./ArraySectionItem";


export enum DefaultValueConstant {
    CurrentBranch = 'CONSTANT_CURRENT_BRANCH',
    Now = 'CONSTANT_NOW',
    FirstDayOfCurrentMonth = 'CONSTANT_CURRENT_MONTH_FIRST_DAY',
    FDOCM = 'FDOCM',
    FDONM = 'FDONM',
    FirstDayOfNextMonth = 'CONSTANT_NEXT_MONTH_FIRST_DAY',
    Tomorrow = 'CONSTANT_TOMORROW',
}

export class Question {

    public regex?: RegExp;
    public options: Choice[] = [];
    public conditions: Condition[] = [];
    public response?: Response;
    public changedEvent = new TypedEvent();
    public validEvent = new TypedEvent();
    public extraEvent = new TypedEvent();
    public savedEvent = new TypedEvent();
    public requiredMessage = 'le champ' + this.label + ' est réquis';
    public message = '';
    public good = true;
    public entityChoices: EntityChoice[] = [];
    public currentResponse?: Response;
    public loadedResponse?: Response;
    public loadFromServer = false;
    public originalId: number = 0;
    public arraySectionItem?: ArraySectionItem;
    public children: Question[] = [];
    public position = 0;
    public parent: Question | null = null;

    constructor(
        public id: number,
        public responseType: ResponseType,
        public label: string,
        public section: Section,
        public required: boolean = false,
        public multiple: boolean = false,
        public regexString: string = '',
        public description: string = '',
        public errorMessage: string = '',
        public filteredByQuestionId: number = 0,
        public hasComment: boolean = false,
        public conditionIsOr = false,
        public displayWidth = 1,
        public placeholder: string = '',
        public inline = false,
        public unit: string = '',
        public defaultValue: string | null = null,
        public parentId: number | null = null,
    ) {
        if (regexString) {
            this.regex = new RegExp(regexString);
        }
        this.changedEvent.on(() => {
            if (this.parent) {
                this.parent.changedEvent.emit('');
            }
            if (this.responseType.template === 'sub-question') {
                this.validate();
            }
        });
        this.validEvent.on(() => {
            this.saveAnswer();
        });
    }

    public canDisplayAsArray() {
        if (this.arraySectionItem) {
            return this.arraySectionItem.allConditionsFulfilled();
        }
        return false;
    }

    public canDisplay() {
        if (!this.section.allConditionsFulfilled()) {
            return false;
        }
        if (0 === this.conditions.length) {
            return true;
        }
        return this.conditionIsOr ?
            this.conditions.some((condition: Condition) => condition.isFulfilled) :
            this.conditions.every((condition: Condition) => condition.isFulfilled);
    }

    public isLastOfSection() {
        const length = this.section.questions.length;
        if (length) {
            const last = this.section.questions[this.section.questions.length - 1];
            // console.log(last);
            if (last.id === this.id) {
                return true;
            }
        }
        return false;
    }

    public currentResponseIsEmpty() {
        return !this.currentResponse || (!this.currentResponse.value.length && 0 === this.currentResponse.valueId);
    }

    loadingFromServer(response: Response) {
        this.loadFromServer = true;
        // console.log('load '+ this.label +' from server is ' + this.loadFromServer);
        this.currentResponse = response;
    }


    public stepValidation(): boolean {
        if (this.responseType.template === 'sub-question') {
            const displayed = this.children.filter((child: Question) => {
                // console.log(this.label +' child '+child.label+' display : '+child.canDisplay()+' , valid : '+child.stepValidation())
                return child.canDisplay()
            });

            // console.log('====>',displayed)
            return displayed.every((child: Question) => child.stepValidation());
        }

        const answerIsEmpty = this.currentResponseIsEmpty();

        if ((this.required && answerIsEmpty) || !this.good) {
            return false;
        }
        return (!this.required && answerIsEmpty) || !this.regex || this.regex.test(this.currentResponse!.value);

    }

    public prevalidate() {
        if (this.responseType.template === 'sub-question') {
            const displayed = this.children.filter((child: Question) => child.canDisplay());
            this.good = displayed.every((child: Question) => {
                return child.stepValidation();
            });
            return;
        }
        const currentResponse = this.currentResponse;
        const answerIsEmpty = this.currentResponseIsEmpty();
        if (answerIsEmpty) {
            this.good = true;
            return;
        }
        if (this.required && answerIsEmpty) {
            this.message = this.requiredMessage;
            this.good = false;
        } else {
            if (!this.required && answerIsEmpty) {
                this.good = true;
            } else {
                if (!this.regex) {
                    this.good = true;
                } else {
                    if (this.regex.test(currentResponse!.value)) {
                        this.good = true;
                    } else {
                        this.message = this.errorMessage;
                        this.good = false;
                    }
                }
            }
        }
    }

    public validate() {
        this.prevalidate();
        if (this.good) {
            this.validEvent.emit('');
        }
    }

    public saveAnswer() {
        // if (this.loadFromServer === true) {
        //     this.loadFromServer = false;
        //     return;
        // }
        const promise = api.saveAnswer(this);
        if (promise) {
            promise.then((axiosResponse: any) => {
                if (axiosResponse.data.code === 700) {
                    this.message = axiosResponse.data.data;
                    store.commit('throwError', this.message);
                    this.good = false;
                    this.savedEvent.emit({ status: 'failure' });
                } else {
                    this.savedEvent.emit({ status: 'success' });
                }
                store.commit("saved");
                this.section.step.validate();
            });
        }
    }

    public isResponseCurrent() {
        return this.currentResponse && this.currentResponse.responseGroup === store.state.currentResponseGroup;
    }

    public hasIcons() {
        return this.originalId ?
            IconRegistry.questionHasChoiceIcons(this.originalId) :
            IconRegistry.questionHasChoiceIcons(this.id);
    }

    public getChoiceIcon(choiceCode: string) {
        return this.originalId ?
            IconRegistry.getChoiceIcon(this.originalId, choiceCode) :
            IconRegistry.getChoiceIcon(this.id, choiceCode);
    }

    public hasHelper() {
        if (this.description) {
            return this.description.trim().length > 0;
        }
        return false;
    }

    public static transform(response: any, responseType: string) {
        if (responseType === 'datepicker') {
            return DatePickerQuestion.transform(response as string);
        }
        if (responseType === 'number') {
            return NumberQuestion.transform(response as string);
        }
        return response;
    }

    public static transformBack(response: any, responseType: string) {
        if (responseType === 'datepicker') {
            const transformBack = DatePickerQuestion.transformBack(response as string);
            return DatePickerQuestion.transformBack(response as string);
        }
        if (responseType === 'number') {
            return NumberQuestion.transformBack(response as string);
        }
        return response;
    }

    public updateDefaultValue() {
        if (!this.currentResponseIsEmpty()) {
            return;
        }
        if (!this.defaultValue) {
            return;
        }
        if (Object.values(DefaultValueConstant).includes(this.defaultValue)) {
            this.updateConstantValue(this.defaultValue as DefaultValueConstant);
        } else if (this.responseType.template === 'radio') {
            this.updateOption(this.defaultValue);
        } else if (this.responseType.template === 'entity') {
        } else {
            this.currentResponse!.value = this.defaultValue;
        }
        this.changedEvent.emit('');
        this.validate();
    }

    private updateOption(code: string) {
        const option = this.options.find((choice: Choice) => choice.code === code);
        if (!option) {
            console.warn(
                'Option ' +
                this.defaultValue +
                ' is not existing in the current list of options for the question ' + this.label
            );
            return;
        }
        this.currentResponse!.value = option.code;
        this.currentResponse!.valueId = option.id;
    }

    private updateConstantValue(constant: DefaultValueConstant) {
        switch (constant) {
            case DefaultValueConstant.CurrentBranch: {
                this.currentResponse!.valueId = store.state.currentResponseGroup.branch.id;
                this.currentResponse!.value = store.state.currentResponseGroup.branch.id;
                break;
            }
            case DefaultValueConstant.Now: {
                this.currentResponse!.value = moment(new Date()).format(DatePickerQuestion.displayFormat);
                break;
            }
            case DefaultValueConstant.Tomorrow: {
                const tomorrow = moment(new Date()).add(1, 'days');
                this.currentResponse!.value = tomorrow.format(DatePickerQuestion.displayFormat);
                break;
            }
            case DefaultValueConstant.FirstDayOfCurrentMonth: {
                var date = new Date(), y = date.getFullYear(), m = date.getMonth();
                var firstDay = new Date(y, m, 1);
                const firstDayOfCurrentMonth = moment(firstDay);
                this.currentResponse!.value = firstDayOfCurrentMonth.format(DatePickerQuestion.displayFormat);
                break;
            }
            case DefaultValueConstant.FirstDayOfNextMonth: {
                var now = new Date();
                if (now.getMonth() == 11) {
                    var current = moment(new Date(now.getFullYear() + 1, 0, 1));
                } else {
                    var current = moment(new Date(now.getFullYear(), now.getMonth() + 1, 1));
                }
                this.currentResponse!.value = current.format(DatePickerQuestion.displayFormat);
                break;
            }
        }
    }
}
